import { fetchApi } from './client';
import type { ProviderOptions } from './vendors';
import type {
    CandidateEntry,
    CandidatePaginatedResponse,
    CandidateSortField,
    SetMonitoredResult,
    SortOrder,
} from './types';

interface ListCandidatesParams {
    q?: string;
    limit?: number;
    offset?: number;
    sort?: CandidateSortField;
    order?: SortOrder;
    /** Restrict to these provider abbreviations (e.g. `['SB', 'MANUAL']`). Empty = no filter. */
    providers?: string[];
    /**
     * Restrict to orphan records: non-curated candidates with zero linked tenants
     * (`tenant_count = 0`) — those left behind after a reconcile remap (VI-765).
     */
    orphan?: boolean;
    /** Restrict to candidates with a finished OSINT report. */
    hasOsintReport?: boolean;
}

/**
 * List catalog product candidates (pre-curation staging layer).
 */
export async function listCandidates(
    params: ListCandidatesParams = {},
): Promise<CandidatePaginatedResponse<CandidateEntry>> {
    const searchParams = new URLSearchParams();

    if (params.q) {
        searchParams.set('q', params.q);
    }

    if (params.limit !== undefined) {
        searchParams.set('limit', String(params.limit));
    }

    if (params.offset !== undefined) {
        searchParams.set('offset', String(params.offset));
    }

    if (params.sort) {
        searchParams.set('sort', params.sort);
    }

    if (params.order) {
        searchParams.set('order', params.order);
    }

    if (params.providers && params.providers.length > 0) {
        searchParams.set('providers', params.providers.join(','));
    }

    if (params.orphan) {
        searchParams.set('orphan', 'true');
    }

    if (params.hasOsintReport) {
        searchParams.set('has_osint_report', 'true');
    }

    const queryString = searchParams.toString();
    const path = `/api/vendor-candidates${queryString ? `?${queryString}` : ''}`;

    return fetchApi<CandidatePaginatedResponse<CandidateEntry>>(path);
}

/** Distinct providers present in the Candidate Catalog. */
export async function listCandidateProviders(): Promise<ProviderOptions> {
    return fetchApi<ProviderOptions>('/api/vendor-candidates/providers');
}

/**
 * Fetch a single candidate by auto-id (used by the reviewer detail view).
 */
export async function getCandidate(id: number): Promise<CandidateEntry> {
    return fetchApi<CandidateEntry>(`/api/vendor-candidates/${String(id)}`);
}

/**
 * Enable/disable continuous CVE monitoring for a candidate (VI-556). Flag-flip
 * only — when the response's `transitioned_on` is true (a 0→1 flip), the caller
 * follows up with a candidate-scoped full re-match to backfill pre-existing CVEs.
 */
export async function setCandidateMonitored(
    id: number,
    monitored: boolean,
): Promise<SetMonitoredResult> {
    return fetchApi<SetMonitoredResult>(`/api/vendor-candidates/${String(id)}/monitored`, {
        method: 'PATCH',
        body: JSON.stringify({ monitored }),
    });
}

/** One promoted candidate in a bulk-promote outcome. */
export interface PromotedCandidate {
    id: number;
    vendor_directory_id: string;
}

/** One failed candidate — not found, no name, or any other error. */
export interface FailedCandidate {
    id: number;
    error: string;
}

/**
 * Outcome of a bulk promote: every input id lands in exactly one bucket. The
 * server promotes each candidate from its OWN stored row (there is no
 * client-supplied body, so nothing can drift from the candidate) — a candidate
 * that is not found or has no name comes back in `failed` with the reason.
 */
export interface PromoteCandidatesResult {
    promoted: PromotedCandidate[];
    failed: FailedCandidate[];
}

/**
 * Promote candidates by id (bulk). The server curates each candidate in place
 * from its stored data; `force` bypasses the duplicate-company auto-link for
 * every id. A single-candidate promote is just a one-element `ids`.
 */
export async function promoteCandidates(
    ids: number[],
    options: { force?: boolean } = {},
): Promise<PromoteCandidatesResult> {
    return fetchApi<PromoteCandidatesResult>('/api/vendor-candidates/promote', {
        method: 'POST',
        body: JSON.stringify({ ids, force: options.force }),
    });
}

/** One failed candidate delete — the candidate id plus the server's reason. */
export interface FailedCandidateDelete {
    candidate_id: number;
    error: string;
}

/**
 * Outcome of a bulk delete: every input id lands in exactly one bucket.
 *
 * - `deleted` – candidate ids that were soft-deleted (removed from the review
 *               queue).
 * - `skipped` – candidate ids already gone (soft-deleted by an earlier op).
 * - `failed`  – any other error, keyed by `candidate_id` with the reason.
 */
export interface BulkDeleteCandidatesResult {
    deleted: number[];
    skipped: number[];
    failed: FailedCandidateDelete[];
}

/**
 * Bulk-delete non-curated Candidate Catalog entries by their numeric id. One
 * request (1–250 ids); the server soft-deletes each candidate, removing it from
 * the review queue. Ids already gone come back in `skipped` rather than failing.
 */
export async function bulkDeleteCandidates(ids: number[]): Promise<BulkDeleteCandidatesResult> {
    return fetchApi<BulkDeleteCandidatesResult>('/api/vendor-candidates/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids }),
    });
}
